import React, { useEffect, useRef, useState } from 'react';
import { Copy, PencilSimple, Check, X, PaperPlaneTilt, SpinnerGap } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { OtoMessageText } from './OtoMessageText';
import { copyText } from '../../utils/clipboard';

export const OtoUserPrompt = ({ message, editing, disabled, onEdit, onCancel, onResend }) => {
  const [value, setValue] = useState(message.text);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const [sendError, setSendError] = useState('');
  const [sending, setSending] = useState(false);
  const input = useRef(null);
  const timer = useRef(null);
  const alive = useRef(true);
  const submitting = useRef(false);
  const copying = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; clearTimeout(timer.current); }; }, []);
  useEffect(() => {
    if (editing) {
      setValue(message.text); setSendError('');
      input.current?.focus(); input.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [editing, message.id, message.text]);
  const copy = async () => {
    if (copying.current) return;
    copying.current = true; setCopyError(''); setCopied(false); clearTimeout(timer.current);
    try {
      await copyText(message.text);
      if (alive.current) { setCopied(true); timer.current = setTimeout(() => setCopied(false), 2200); }
    } catch { if (alive.current) setCopyError('Copie refusée par le navigateur. Vous pouvez sélectionner le texte pour le copier.'); }
    finally { copying.current = false; }
  };
  const resend = async event => {
    event.preventDefault();
    if (submitting.current || disabled || !value.trim()) return;
    submitting.current = true; setSending(true); setSendError('');
    try {
      const accepted = await onResend(message, value.trim());
      if (!accepted && alive.current) setSendError('Envoi non confirmé. Votre correction est conservée.');
    } catch { if (alive.current) setSendError('Envoi non confirmé. Votre correction est conservée.'); }
    finally { submitting.current = false; if (alive.current) setSending(false); }
  };
  return <div className="min-w-0" data-testid={`oto-user-prompt-${message.id}`}>
    {editing ? <form onSubmit={resend} className="space-y-3" data-testid={`oto-edit-form-${message.id}`}>
      <label htmlFor={`oto-edit-${message.id}`} className="text-xs font-semibold text-[#2D5A4C]">Modifier le prompt</label>
      <Textarea ref={input} id={`oto-edit-${message.id}`} rows={4} maxLength={6000} value={value} onChange={event => setValue(event.target.value)} disabled={disabled || sending} className="min-h-[110px] resize-y bg-white text-sm" data-testid={`oto-edit-input-${message.id}`} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] text-[#5E6D66]" data-testid={`oto-edit-count-${message.id}`}>{value.length}/6000</span>
        <div className="flex flex-wrap gap-2"><Button type="button" variant="ghost" size="sm" disabled={disabled || sending} onClick={onCancel} data-testid={`oto-edit-cancel-${message.id}`}><X size={15} className="mr-1" />Annuler</Button><Button type="submit" size="sm" className="bg-[#2D5A4C] hover:bg-[#1E4136]" disabled={disabled || sending || !value.trim()} data-testid={`oto-edit-resend-${message.id}`}>{sending ? <SpinnerGap size={15} className="mr-1 animate-spin" /> : <PaperPlaneTilt size={15} className="mr-1" />}{sending ? 'Envoi…' : 'Renvoyer'}</Button></div>
      </div>
      <p className="text-[11px] text-[#5E6D66]" data-testid={`oto-edit-policy-${message.id}`}>Réponse uniquement · historique conservé · aucune action rejouée.</p>
      {sendError && <p role="alert" className="text-xs text-red-700" data-testid={`oto-edit-error-${message.id}`}>{sendError}</p>}
    </form> : <OtoMessageText text={message.text} testId={`oto-message-text-${message.id}`} />}
    <div className="mt-2 flex flex-wrap justify-end gap-1">
      <Button type="button" size="sm" variant="ghost" onClick={copy} aria-label="Copier ce prompt" title="Copier ce prompt" data-testid={`oto-prompt-copy-${message.id}`}>{copied ? <Check size={15} className="mr-1" /> : <Copy size={15} className="mr-1" />}<span role="status" data-testid={`oto-prompt-copy-status-${message.id}`}>{copied ? 'Copié' : 'Copier'}</span></Button>
      {!editing && <Button type="button" size="sm" variant="ghost" disabled={disabled || message.id.startsWith('local-')} onClick={onEdit} aria-label="Modifier ce prompt" title="Modifier ce prompt" data-testid={`oto-prompt-edit-${message.id}`}><PencilSimple size={15} className="mr-1" />Modifier</Button>}
    </div>
    {copyError && <p role="alert" className="mt-1 text-xs text-red-700" data-testid={`oto-prompt-copy-error-${message.id}`}>{copyError}</p>}
  </div>;
};